/* src/components/QiblaFinder.js */
import React, { useState, useEffect, useRef } from 'react';
import { Compass, Clock, MapPin, AlertCircle } from 'lucide-react';
import { ArrowUp01Icon, Kaaba01Icon } from "hugeicons-react";
import { useDarkMode } from '../contexts/DarkModeContext';
import { usePermissions } from '../hooks/usePermissions';
import { calculateQibla } from '../utils/qiblaCalculator';
import { useLocation } from '../contexts/LocationContext';
import { useBridge } from '../contexts/BridgeContext';

const QiblaFinder = () => {
  const { isDarkMode } = useDarkMode();
  const { location: locationPermission, motion: motionPermission, checkPermissions, requestPermissions } = usePermissions();
  const { location, error: locationError, loading: locationLoading, getLocation } = useLocation();
  const [qiblaDirection, setQiblaDirection] = useState(null);
  const [deviceDirection, setDeviceDirection] = useState(null);
  const [error, setError] = useState(null);
  const [isMobileDevice, setIsMobileDevice] = useState(true);
  const [componentState, setComponentState] = useState('initializing'); // Use the new type
  const [demoMode, setDemoMode] = useState(false); // New: demo mode state

  const compassTimeoutRef = useRef(null); // Ref for the compass timeout
  const COMPASS_TIMEOUT_DURATION = 20000; // 20 seconds (increased from 7 seconds)

  const readings = useRef([]);
  const READINGS_BUFFER_SIZE = 10;
  const isNative = typeof window !== 'undefined' && window.isUmmahtiNative;
  const hasInitializedRef = useRef(false);
  const [alignmentStatus, setAlignmentStatus] = useState('none');
  const lastVibrationTime = useRef(0);
  
  // More precise thresholds
  const THRESHOLDS = {
    ALIGNED: 1, // ±1 degree - perfect alignment
    CLOSE: 2.5, // ±2.5 degrees - very close
    NEAR: 5, // ±5 degrees - getting close
  };
  const VIBRATION_COOLDOWN = 1000;

  // Add a ref to track the start time for timeout handling
  const startTimeRef = useRef(Date.now());

  // Add state for delayed rendering to prevent flickering
  const [stableComponentState, setStableComponentState] = useState('initializing');
  
  const { isBridgeReady } = useBridge();
  
  // Place all hooks at the top level (never inside conditionals)
  const [demoDeviceDirection, setDemoDeviceDirection] = useState(45); // For demo mode
  const [lastValidDirectionTime, setLastValidDirectionTime] = useState(Date.now());
  const [showSensorWait, setShowSensorWait] = useState(false);
  
  // Add a ref to track location retry attempts
  const locationRetryCount = useRef(0);
  const MAX_LOCATION_RETRIES = 5;
  
  // Use effect to stabilize state transitions and prevent flickering
  useEffect(() => {
    // Check if we're in the native app environment
    const isNativeApp = typeof window !== 'undefined' && window.isUmmahtiNative;
    
    // Don't delay certain state transitions
    if (componentState === 'ready' || componentState === 'demoMode') {
      setStableComponentState(componentState);
      return;
    }
    
    // Special handling for native apps to prevent error flashes
    if (isNativeApp) {
      // For native apps, prioritize loading states over error states
      // This prevents brief error flashes while native bridge is connecting
      if (componentState === 'locationError' && stableComponentState === 'gettingLocation') {
        console.log('[QiblaFinder] Suppressing brief location error in native app');
        
        // Keep showing loading state for longer
        const suppressErrorTimer = setTimeout(() => {
          // Only show error if we're still in error state after delay
          if (componentState === 'locationError') {
            console.log('[QiblaFinder] Error persists, now showing error state');
            setStableComponentState(componentState);
          }
        }, 2000);
        
        return () => clearTimeout(suppressErrorTimer);
      }
    }
    
    // For other states, add a small delay before updating the UI
    const timer = setTimeout(() => {
      setStableComponentState(componentState);
    }, 300); // Short delay to prevent flickering
    
    return () => clearTimeout(timer);
  }, [componentState]);

  // Check for simulator immediately on component mount - before any other checks
  useEffect(() => {
    const checkSimulator = async () => {
      try {
        console.log('[QiblaFinder] Starting simulator detection...');
        
        // Check for global variable first (fastest)
        if (typeof window.isSimulatorEnvironment === 'boolean') {
          console.log('[QiblaFinder] Found global isSimulatorEnvironment:', window.isSimulatorEnvironment);
          if (window.isSimulatorEnvironment) {
            console.log('[QiblaFinder] Global variable indicates simulator, enabling demo mode');
            setDemoMode(true);
            setComponentState('demoMode');
            setError(null);
            return true;
          }
        }
        
        // Check if we're in a simulator using the native bridge
        if (window.ummahtiIosBridge && typeof window.ummahtiIosBridge.isSimulator === 'function') {
          console.log('[QiblaFinder] Checking if running in simulator via native bridge...');
          const isSimulator = await window.ummahtiIosBridge.isSimulator();
          console.log('[QiblaFinder] Native bridge reports simulator:', isSimulator);
          
          if (isSimulator) {
            console.log('[QiblaFinder] Running in simulator, enabling demo mode');
            setDemoMode(true);
            setComponentState('demoMode');
            setError(null);
            return true;
          }
        }
        
        // Try the message handler as a last resort
        if (window.webkit?.messageHandlers?.['check-simulator']) {
          console.log('[QiblaFinder] Requesting simulator status via message handler');
          window.webkit.messageHandlers['check-simulator'].postMessage({});
        }
        
        return false;
      } catch (error) {
        console.error('[QiblaFinder] Error checking simulator status:', error);
        return false;
      }
    };
    
    // Run the check immediately
    checkSimulator();
    
    // Also listen for the simulator-status event
    const handleSimulatorStatus = (event) => {
      console.log('[QiblaFinder] Received simulator-status event:', event.detail);
      if (event.detail?.isSimulator) {
        console.log('[QiblaFinder] Event confirms simulator environment, enabling demo mode');
        setDemoMode(true);
        setComponentState('demoMode');
        setError(null);
      }
    };
    
    window.addEventListener('simulator-status', handleSimulatorStatus);
    
    // Clean up
    return () => {
      window.removeEventListener('simulator-status', handleSimulatorStatus);
    };
  }, []); // Empty dependency array means this runs once on mount

  // Force demo mode for simulator after a short delay if other methods fail
  useEffect(() => {
    // Last resort: check for simulator after a short delay
    const timer = setTimeout(() => {
      if (componentState === 'waitingForCompass' || componentState === 'gettingLocation' || 
          componentState === 'checkingPermissions' || componentState === 'requestingPermissions') {
        
        console.log('[QiblaFinder] Still waiting for sensors, checking simulator status one more time');
        
        // Try all methods again
        if (window.isSimulatorEnvironment || 
            (window.navigator.platform && /Simulator|x86_64|i386|arm64|Generic/i.test(window.navigator.platform))) {
          console.log('[QiblaFinder] Detected simulator in delayed check, forcing demo mode');
          setDemoMode(true);
          setComponentState('demoMode');
          setError(null);
        } else if (window.webkit?.messageHandlers?.['check-simulator']) {
          console.log('[QiblaFinder] Last attempt: requesting simulator check via message handler');
          window.webkit.messageHandlers['check-simulator'].postMessage({});
        }
      }
    }, 3000); // Check after 3 seconds
    
    return () => clearTimeout(timer);
  }, [componentState]);

  // 1. Check Device Compatibility & Initial Permission Check
  useEffect(() => {
    // Wait for bridge to be ready on native before proceeding
    if (isNative && !isBridgeReady) {
      return;
    }
    const checkDeviceAndPermissions = async () => {
      // Don't proceed if we're already in demo mode
      if (demoMode) {
        console.log('[QiblaFinder] Already in demo mode, skipping device checks');
        return;
      }
      
      setComponentState('initializing'); // Start as initializing
      
      // Check for native app environment
      const isNativeApp = typeof window !== 'undefined' && window.isUmmahtiNative;
      console.log('[QiblaFinder] Native app environment detected:', isNativeApp);
      
      // --- DEMO MODE/SIMULATOR DETECTION ---
      const userAgent = navigator.userAgent || '';
      const isSimulator =
        /Simulator|x86_64|i386|arm64|Generic/i.test(userAgent) ||
        (window.navigator.platform &&
          /Simulator|x86_64|i386|arm64|Generic/i.test(window.navigator.platform));
      // Optionally, check for a global injected by your native bridge
      let nativeSaysSimulator = false;
      if (window.ummahtiIosBridge && typeof window.ummahtiIosBridge.isSimulator === 'function') {
        try {
          nativeSaysSimulator = await window.ummahtiIosBridge.isSimulator();
        } catch (e) {
          nativeSaysSimulator = false;
        }
      }
      const mobileCheck = /iPhone|iPad|iPod|Android/i.test(userAgent);
      const hasOrientation = window.DeviceOrientationEvent !== undefined;
      const hasGeolocation = 'geolocation' in navigator;
      // Simulators might report true for geolocation/orientation but lack real sensors
      const mobileWithSensors = mobileCheck && hasOrientation && hasGeolocation;

      // --- DEMO MODE LOGIC ---
      if (!mobileWithSensors || isSimulator || nativeSaysSimulator) {
        setDemoMode(true);
        setComponentState('demoMode');
        setError(null);
        return;
      }
      setIsMobileDevice(mobileWithSensors);
      setComponentState('checkingPermissions'); // Move to checking permissions
      
      // For native apps, we need to ensure we have the right permissions
      if (isNativeApp) {
        console.log('[QiblaFinder] Using native app location flow');
        
        // In native apps, we can directly request location after permissions
        if (typeof checkPermissions === 'function') {
          try {
            await checkPermissions();
            
            // Once permissions are checked, immediately move to getting location
            setComponentState('gettingLocation');
            setError(null);
            
            try {
              // Request location through the native bridge
              if (window.webkit?.messageHandlers?.locationHandler?.postMessage) {
                console.log('[QiblaFinder] Requesting location via webkit message handler');
                window.webkit.messageHandlers.locationHandler.postMessage({action: 'requestLocation'});
              } else if (window.ummahti?.requestLocation) {
                console.log('[QiblaFinder] Requesting location via ummahti interface');
                window.ummahti.requestLocation();
              }
              
              // Also call getLocation which sets up listeners
              if (typeof getLocation === 'function') {
                await getLocation();
              }
            } catch (locationErr) {
              console.error("Error getting location:", locationErr);
              // Don't show error yet, let the location handler deal with it
            }
          } catch (permError) {
            console.error("Error checking permissions:", permError);
            setComponentState('permissionsDenied');
            setError(`To use the Qibla finder, please allow access to your location and motion sensors.`);
          }
        } else {
          // If we can't check permissions in the native app, try to get location directly
          setComponentState('gettingLocation');
          
          try {
            if (typeof getLocation === 'function') {
              await getLocation();
            }
          } catch (locationErr) {
            console.error("Error getting location:", locationErr);
            // Don't show error yet, let the location handler deal with it
          }
        }
        return; // End native app flow
      }
      
      // Continue with web flow
      // Check if the permissions function exists before calling it
      if (typeof checkPermissions === 'function') {
        try {
          await checkPermissions();
          
          // Check if permissions are already granted
          if (locationPermission === 'granted' && motionPermission === 'granted') {
            setComponentState('gettingLocation');
            setError(null);
            
            try {
              await getLocation();
            } catch (locationErr) {
              console.error("Error getting location:", locationErr);
              setComponentState('locationError');
              setError(`Could not get your location: ${locationErr.message}`);
            }
          }
        } catch (permError) {
          console.error("Error checking permissions:", permError);
          // Don't show the technical error to users, use a more friendly message
          setComponentState('permissionsDenied');
          setError(`To use the Qibla finder, please allow access to your location and motion sensors.`);
        }
      } else {
        console.warn("[QiblaFinder] checkPermissions is not a function, proceeding anyway");
        // If we can't check permissions, try to get the location anyway
        setComponentState('gettingLocation');
        
        try {
          if (typeof getLocation === 'function') {
            await getLocation();
          } else {
            throw new Error('Location function not available');
          }
        } catch (locationErr) {
          console.error("Error getting location:", locationErr);
          setComponentState('locationError');
          setError(`Could not access your location. Please ensure location services are enabled.`);
        }
      }
    };

    checkDeviceAndPermissions();
  }, [checkPermissions, locationPermission, motionPermission, getLocation, isBridgeReady]);

  // 2. Handle Permission Status Changes - Add safety checks here too
  useEffect(() => {
    // Only react when checking or requesting permissions
    if (componentState !== 'checkingPermissions' && componentState !== 'requestingPermissions') return;

    // Handle potential undefined values more safely
    const locationStatus = locationPermission || 'unknown';
    const motionStatus = motionPermission || 'unknown';
    
    console.log(`[QiblaFinder] Permission status - Location: ${locationStatus}, Motion: ${motionStatus}`);

    if (locationStatus === 'granted' && motionStatus === 'granted') {
      // Permissions granted, proceed to get location
      console.log('[QiblaFinder] Both permissions granted, moving to gettingLocation state');
      setComponentState('gettingLocation'); // New state
      setError(null); // Clear any previous permission errors
      
      // Explicitly request location
      if (typeof getLocation === 'function') {
        getLocation().catch(err => {
          console.error("Error getting location:", err);
          setComponentState('locationError');
          setError(`Could not get your location. Please check that location services are enabled.`);
        });
      } else {
        console.error("getLocation is not a function");
        setComponentState('locationError');
        setError("Location services unavailable");
      }

    } else if (locationStatus === 'denied' || motionStatus === 'denied') {
      console.log('[QiblaFinder] Permission denied, moving to permissionsDenied state');
      setComponentState('permissionsDenied');
      setError('Location and Motion permissions are required. Please enable them in your device settings.');
    } else if (locationStatus === 'prompt' || motionStatus === 'prompt') {
      // If still prompting after trying to request, stay in requesting state
      if (componentState !== 'requestingPermissions') {
          console.log('[QiblaFinder] Permission prompt required, moving to requestingPermissions state');
          setComponentState('requestingPermissions');
          
          if (typeof requestPermissions === 'function') {
            try {
              requestPermissions();
            } catch (reqError) {
              console.error("Error requesting permissions:", reqError);
              setComponentState('permissionsDenied');
              setError(`Please enable location and motion permissions in your device settings.`);
            }
          } else {
            console.error("requestPermissions is not a function");
            // Fall back to a timeout-based approach
            setTimeout(() => {
              // After a short delay, try to proceed to location anyway
              setComponentState('gettingLocation');
            }, 2000);
          }
      }
    } else {
      // Handle unknown status - don't get stuck
      console.warn(`[QiblaFinder] Unknown permission status combination. Location: ${locationStatus}, Motion: ${motionStatus}`);
      if (componentState === 'checkingPermissions' && Date.now() - startTimeRef.current > 5000) {
        // If checking permissions for too long (5s), try to proceed anyway
        setComponentState('gettingLocation');
        console.log('[QiblaFinder] Permission check timeout, attempting to proceed anyway');
      }
    }

  }, [locationPermission, motionPermission, componentState, requestPermissions, getLocation]);

  // 3. Handle Location Status
  useEffect(() => {
      if (componentState !== 'gettingLocation') return;

      // Check if we're in the native app environment
      const isNativeApp = typeof window !== 'undefined' && window.isUmmahtiNative;

      if (location && location.latitude != null && location.longitude != null) {
          // Location acquired, calculate Qibla and wait for compass
          try {
              const qibla = calculateQibla(location.latitude, location.longitude);
              setQiblaDirection(qibla);
              setError(null); // Clear previous errors
              setComponentState('waitingForCompass'); // Move to waiting for compass
              locationRetryCount.current = 0; // Reset retry count
              console.log(`[QiblaFinder] Qibla calculated: ${qibla} for location:`, location);
          } catch (calcError) {
              console.error("[QiblaFinder] Error calculating Qibla:", calcError);
              setComponentState('locationError'); // Use locationError state
              setError("Could not calculate Qibla direction.");
              setQiblaDirection(null);
          }
      } else if (locationError && !isNativeApp) {
          // Only show location errors immediately for non-native environments
          setComponentState('locationError');
          setError(`Location error: ${locationError}`);
          setQiblaDirection(null);
      } else {
          // For native apps or when still loading, stay in loading state longer
          // This prevents the error flash on iOS
          const timeoutDuration = isNativeApp ? 5000 : 2000;
          const errorTimer = setTimeout(() => {
              if (componentState === 'gettingLocation') {
                  if (!location || location.latitude == null || location.longitude == null) {
                      if (isNativeApp) {
                          // For native apps, keep retrying and do NOT show error
                          locationRetryCount.current += 1;
                          console.log(`[QiblaFinder] Native: Location still not available after timeout (retry ${locationRetryCount.current})`);
                          // Try to request location again
                          if (window.webkit?.messageHandlers?.locationHandler?.postMessage) {
                              window.webkit.messageHandlers.locationHandler.postMessage({action: 'requestLocation'});
                          } else if (window.ummahti?.requestLocation) {
                              window.ummahti.requestLocation();
                          }
                          // Optionally, after several retries, show a gentle nudge (not an error)
                          if (locationRetryCount.current >= MAX_LOCATION_RETRIES) {
                              setError('Still waiting for location data... Please ensure location services are enabled.');
                          } else {
                              setError(null); // No error, just keep waiting
                          }
                          // Do NOT set componentState to 'locationError' here
                      } else {
                          // For non-native, show error now
                          setComponentState('locationError');
                          setError('Unable to get your location. Please check your device settings.');
                      }
                  }
              }
          }, timeoutDuration);
          return () => clearTimeout(errorTimer);
      }

  }, [location, locationError, locationLoading, componentState]);

  // 4. Start/Stop Compass and Set Timeout
  useEffect(() => {
    if (!isNative || !window.ummahti?.watchCompass || !window.ummahti?.stopWatchingCompass) return;

    // Only start compass when entering a state that needs it
    if (componentState === 'waitingForCompass' || componentState === 'ready') {
      console.log('[QiblaFinder] Native: Starting compass (watchCompass)');
      window.ummahti.watchCompass();

      // Cleanup: stop compass when leaving these states or on unmount
      return () => {
        console.log('[QiblaFinder] Native: Stopping compass (stopWatchingCompass)');
        window.ummahti.stopWatchingCompass();
      };
    }
    // If not in a compass-using state, do nothing (don't start/stop repeatedly)
  }, [componentState, isNative]);

  // 5. Native Compass Listener & Transition to Ready State
  useEffect(() => {
    if (!isNative) return; // Only for native app

    const handleCompassUpdate = (event) => {
      if (!event?.detail) return;
      const { heading } = event.detail;
      if (typeof heading === 'number' && !isNaN(heading) && heading >= 0 && heading < 360) {
        setDeviceDirection(heading);
        setLastValidDirectionTime(Date.now());
        if (componentState === 'waitingForCompass') {
          if (compassTimeoutRef.current) {
            clearTimeout(compassTimeoutRef.current);
            compassTimeoutRef.current = null;
          }
          setComponentState('ready');
          setError(null);
        } else if (componentState === 'compassError') {
          setComponentState('ready');
          setError(null);
        }
      }
      // Ignore invalid values
    };

    window.addEventListener('native-compass-update', handleCompassUpdate);
    console.log('[QiblaFinder] Native compass listener added.');
    return () => {
      window.removeEventListener('native-compass-update', handleCompassUpdate);
      console.log('[QiblaFinder] Native compass listener removed.');
    };
  }, [isNative, componentState]);

  // Add similar effect for non-native (web orientation) if needed
  useEffect(() => {
      if (isNative) return; // Only for web

      // If we receive a direction reading while waiting, transition to ready
      if (componentState === 'waitingForCompass' && deviceDirection !== null) {
          if (compassTimeoutRef.current) {
              clearTimeout(compassTimeoutRef.current);
              compassTimeoutRef.current = null;
          }
          setComponentState('ready');
           setError(null);
      }
      // Add recovery from 'compassError' for web if needed
      else if (componentState === 'compassError' && deviceDirection !== null) {
          setComponentState('ready');
           setError(null);
      }
  }, [deviceDirection, componentState, isNative]);

  // 6. Calculate Qibla Direction (Should only run in appropriate states)
  useEffect(() => {
    // Only calculate Qibla if in an appropriate state and we have location but no qibla direction yet
    const allowedStates = ['gettingLocation', 'waitingForCompass', 'ready'];
    if (!allowedStates.includes(componentState)) return;
    
    if (location && location.latitude != null && location.longitude != null && qiblaDirection === null) {
      try {
        const qibla = calculateQibla(location.latitude, location.longitude);
        setQiblaDirection(qibla);
        console.log(`[QiblaFinder] Qibla calculated: ${qibla} for location:`, location);
        
        // If we're in gettingLocation, move to waitingForCompass
        if (componentState === 'gettingLocation') {
          setComponentState('waitingForCompass');
        }
      } catch (calcError) {
        console.error("[QiblaFinder] Error calculating Qibla:", calcError);
        // Only change state if we're not already in an error state
        if (componentState !== 'compassError') {
          setError("Could not calculate Qibla direction.");
          setComponentState('locationError');
        }
        setQiblaDirection(null);
      }
    }
  }, [location, qiblaDirection, componentState]);

  // 7. Handle Alignment Status and Vibration (Was 6)
  useEffect(() => {
    if (qiblaDirection === null || deviceDirection === null || componentState !== 'ready') {
      setAlignmentStatus('none');
      return;
    }

    const diff = Math.abs((deviceDirection - qiblaDirection + 360) % 360);
    const normalizedDiff = Math.min(diff, 360 - diff);
    
    let newStatus = 'none';
    if (normalizedDiff <= THRESHOLDS.ALIGNED) newStatus = 'aligned';
    else if (normalizedDiff <= THRESHOLDS.CLOSE) newStatus = 'close';
    else if (normalizedDiff <= THRESHOLDS.NEAR) newStatus = 'near';
    
    setAlignmentStatus(newStatus);

    // Vibration for perfect alignment in native app
    if (newStatus === 'aligned' && isNative && window.ummahti?.vibrate) {
      const now = Date.now();
      if (now - lastVibrationTime.current >= VIBRATION_COOLDOWN) {
        try {
          window.ummahti.vibrate();
          lastVibrationTime.current = now;
          console.log('[QiblaFinder] Vibration triggered');
        } catch (vibError) {
          console.error('[QiblaFinder] Error triggering vibration:', vibError);
        }
      }
    }
  }, [deviceDirection, qiblaDirection, isNative, componentState]); // Added componentState dependency

  // Sensor wait indicator logic
  useEffect(() => {
    if (componentState !== 'ready') {
      setShowSensorWait(false);
      return;
    }
    const check = () => {
      if (Date.now() - lastValidDirectionTime > 3000) {
        setShowSensorWait(true);
      } else {
        setShowSensorWait(false);
      }
    };
    const interval = setInterval(check, 500);
    check();
    return () => clearInterval(interval);
  }, [componentState, lastValidDirectionTime]);

  // Demo mode compass animation effect (must be at top level)
  useEffect(() => {
    if (!demoMode) return; // Only run for demo mode
    const animateCompass = () => {
      const fakeQibla = 120;
      setDemoDeviceDirection(prev => {
        const diff = (fakeQibla - prev + 360) % 360;
        const shortestPath = diff > 180 ? diff - 360 : diff;
        const step = shortestPath > 0 ? Math.min(1, shortestPath) : Math.max(-1, shortestPath);
        return (prev + step + 360) % 360;
      });
    };
    const interval = setInterval(animateCompass, 50);
    return () => clearInterval(interval);
  }, [demoMode]);

  // Update the bridge waiting logic in the component
  // 1. Add a bridge timeout effect for guests
  useEffect(() => {
    // If in native mode but bridge isn't ready, set a timeout to proceed anyway for guest users
    if (isNative && !isBridgeReady) {
      const bridgeTimeout = setTimeout(() => {
        console.log('[QiblaFinder] Bridge initialization taking too long, proceeding anyway for possible guest user');
        // Continue with component initialization even without bridge
        const checkDeviceAndPermissions = async () => {
          setComponentState('initializing');
          
          // Check for demo mode first
          const userAgent = navigator.userAgent || '';
          const isSimulator =
            /Simulator|x86_64|i386|arm64|Generic/i.test(userAgent) ||
            (window.navigator.platform &&
              /Simulator|x86_64|i386|arm64|Generic/i.test(window.navigator.platform));
          
          if (isSimulator) {
            setDemoMode(true);
            setComponentState('demoMode');
            setError(null);
            return;
          }
          
          // Continue with regular initialization
          setComponentState('checkingPermissions');
          
          if (typeof checkPermissions === 'function') {
            try {
              await checkPermissions();
              
              // Once permissions are checked, immediately move to getting location
              setComponentState('gettingLocation');
              setError(null);
              
              if (typeof getLocation === 'function') {
                await getLocation();
              }
            } catch (error) {
              console.error("Error in fallback initialization:", error);
              setComponentState('permissionsDenied');
              setError('To use the Qibla finder, please allow access to your location and motion sensors.');
            }
          }
        };
        
        checkDeviceAndPermissions();
      }, 5000); // Wait 5 seconds for bridge initialization
      
      return () => clearTimeout(bridgeTimeout);
    }
  }, [isNative, isBridgeReady, checkPermissions, getLocation]);

  // Show a special loading state if in native app and bridge is not ready
  if (isNative && !isBridgeReady) {
    return (
      <div className={`min-h-screen flex flex-col relative overflow-x-hidden ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-100'}`}>
        {/* Background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {isDarkMode ? (
            <>
              <div className="absolute -top-1/4 -left-1/4 w-full max-w-[800px] h-full max-h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
              <div className="absolute -bottom-1/4 -right-1/4 w-full max-w-[800px] h-full max-h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-gray-100 via-emerald-100/20 to-gray-100" />
              <div className="absolute inset-0 bg-gradient-to-b from-gray-100/90 via-transparent to-gray-100/90" />
              <div className="absolute inset-0 backdrop-blur-[50px]" />
              <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("/images/bgpattern.png")', backgroundSize: '400px', filter: 'invert(1)', mixBlendMode: 'multiply' }}></div>
            </>
          )}
        </div>
        <div className="relative z-10 flex flex-col h-full flex-1 items-center justify-center">
          <div className={`w-24 h-24 mb-6 rounded-full border-4 animate-spin-slow ${isDarkMode ? 'border-emerald-500/20' : 'border-emerald-500/10'}`}></div>
          <p className={`text-lg font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Preparing sensors...</p>
          <p className={`mt-2 text-base text-center ${isDarkMode ? 'text-emerald-200/80' : 'text-emerald-700/80'}`}>Connecting to device sensors. Please wait.</p>
          <p className={`mt-2 text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Loading will continue automatically...</p>
        </div>
        <style>{`
          @keyframes spin-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          .animate-spin-slow { animation: spin-slow 2.5s linear infinite; }
        `}</style>
      </div>
    );
  }

  // --- Rendering Logic ---

  const getAlignmentStyles = (status = alignmentStatus) => {
    switch (status) {
      case 'aligned':
        return {
          ring: `ring-6 ${isDarkMode ? 'ring-emerald-500/40' : 'ring-emerald-500/30'}`,
          glow: `${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-500/10'}`,
          text: `${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'} font-medium`,
          bg: `${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`
        };
      case 'close':
        return {
          ring: `ring-2 ${isDarkMode ? 'ring-emerald-500/30' : 'ring-emerald-500/20'}`,
          glow: `${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-500/5'}`,
          text: `${isDarkMode ? 'text-emerald-400/80' : 'text-emerald-600/80'}`,
          bg: `${isDarkMode ? 'bg-emerald-500/5' : 'bg-emerald-50/80'}`
        };
      case 'near':
        return {
          ring: `ring-1 ${isDarkMode ? 'ring-emerald-500/20' : 'ring-emerald-500/10'}`,
          glow: `${isDarkMode ? 'bg-emerald-500/5' : 'bg-emerald-500/3'}`,
          text: `${isDarkMode ? 'text-emerald-400/60' : 'text-emerald-600/60'}`,
          bg: `${isDarkMode ? 'bg-emerald-500/3' : 'bg-emerald-50/60'}`
        };
      default:
        return {
          ring: '',
          glow: '',
          text: '',
          bg: ''
        };
    }
  };

  const getAlignmentMessage = () => {
    switch (alignmentStatus) {
      case 'aligned':
        return 'Perfectly aligned';
      case 'close':
        return 'Very close to Qibla';
      case 'near':
        return 'Getting close to Qibla';
      default:
        return '';
    }
  };

  // Updated renderLoadingState to show specific messages
   const renderLoadingState = (state) => {
     let message = "Initializing...";
     let nudge = null;
     switch (state) {
         case 'checkingPermissions':
             message = "Checking permissions...";
             break;
         case 'requestingPermissions':
             message = "Requesting permissions...";
             break;
         case 'gettingLocation':
             message = "Acquiring location...";
             break;
         case 'waitingForCompass':
             message = "Waiting for direction data...";
             nudge = (
               <div
                 className={`mt-6 px-4 py-3 rounded-xl text-base font-medium text-center shadow-sm transition-all duration-300 ${
                   isDarkMode
                     ? 'bg-emerald-900/40 text-emerald-200 border border-emerald-800'
                     : 'bg-emerald-50/80 text-emerald-700 border border-emerald-200'
                 }`}
                 style={{ maxWidth: 360, margin: '0 auto' }}
               >
                 Please gently move your device to activate the compass sensor.
               </div>
             );
             break;
         default: // Keep initializing as fallback
             message = "Initializing...";
             break;
     }

     return (
       <div className={`min-h-screen flex flex-col relative overflow-x-hidden ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-100'}`}>
         {/* Background elements */}
         <div className="absolute inset-0 overflow-hidden">
            {isDarkMode ? (
              <>
                <div className="absolute -top-1/4 -left-1/4 w-full max-w-[800px] h-full max-h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
                <div className="absolute -bottom-1/4 -right-1/4 w-full max-w-[800px] h-full max-h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
              </>
            ) : (
              <>
                <div className="absolute inset-0 bg-gradient-to-b from-gray-100 via-emerald-100/20 to-gray-100" />
                <div className="absolute inset-0 bg-gradient-to-b from-gray-100/90 via-transparent to-gray-100/90" />
                <div className="absolute inset-0 backdrop-blur-[50px]" />
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("/images/bgpattern.png")', backgroundSize: '400px', filter: 'invert(1)', mixBlendMode: 'multiply' }}></div>
              </>
            )}
          </div>
         <div className="relative z-10 flex flex-col h-full flex-1 items-center justify-center">
           <div className={`w-24 h-24 mb-6 rounded-full border-4 animate-pulse ${isDarkMode ? 'border-emerald-500/20' : 'border-emerald-500/10'}`}></div>
           <p className={`text-lg font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{message}</p>
           {nudge}
         </div>
       </div>
     );
   };

  const renderErrorState = (errorMessage) => (
     <div className={`min-h-screen flex flex-col relative overflow-x-hidden ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-100'}`}> 
        {/* Background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {isDarkMode ? (
            <>
              <div className="absolute -top-1/4 -left-1/4 w-full max-w-[800px] h-full max-h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
              <div className="absolute -bottom-1/4 -right-1/4 w-full max-w-[800px] h-full max-h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-gray-100 via-emerald-100/20 to-gray-100" />
              <div className="absolute inset-0 bg-gradient-to-b from-gray-100/90 via-transparent to-gray-100/90" />
              <div className="absolute inset-0 backdrop-blur-[50px]" />
              <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("/images/bgpattern.png")', backgroundSize: '400px', filter: 'invert(1)', mixBlendMode: 'multiply' }}></div>
            </>
          )}
        </div>

        <div className="relative z-10 flex flex-col h-full flex-1 items-center justify-center px-6 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Error</h2>
          <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{errorMessage}</p>
          {/* Allow retrying permissions if that was the specific error */}
          {componentState === 'permissionsDenied' && (
             <button 
                 onClick={requestPermissions} 
                 className={`mt-6 px-4 py-2 rounded-lg ${isDarkMode ? 'bg-emerald-500 text-black' : 'bg-emerald-600 text-white'}`}
             >
                 Retry Permissions
             </button>
          )}
          {/* Optionally add a retry button for location errors? */}
          {/* Optionally add info for compass errors */}
          {componentState === 'compassError' && (
              <p className={`mt-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Try moving your device or ensuring it's calibrated.
              </p>
          )}
        </div>
     </div>
  );

  // --- DEMO MODE RENDERING ---
  // Calculate demo alignment status at component level
  const getDemoAlignmentStatus = () => {
    if (!demoMode) return 'none';
    
    const fakeQibla = 120; // Fixed Qibla direction for demo
    const diff = Math.abs((demoDeviceDirection - fakeQibla + 360) % 360);
    const normalizedDiff = Math.min(diff, 360 - diff);
    
    if (normalizedDiff <= THRESHOLDS.ALIGNED) return 'aligned';
    if (normalizedDiff <= THRESHOLDS.CLOSE) return 'close';
    if (normalizedDiff <= THRESHOLDS.NEAR) return 'near';
    return 'none';
  };
  
  const demoAlignmentStatus = getDemoAlignmentStatus();
  const demoAlignmentStyles = getAlignmentStyles(demoAlignmentStatus);
  
  // Check if we're on iPad Air 5th gen or similar device with phone app ratio
  const isIpadPhoneApp = () => {
    const userAgent = navigator.userAgent || '';
    const isIpad = /iPad/i.test(userAgent);
    const aspectRatio = window.innerWidth / window.innerHeight;
    // iPad Air 5th gen in phone app mode has a narrower aspect ratio
    return isIpad && aspectRatio < 0.75;
  };
  
  const isCompactView = isIpadPhoneApp();

  const renderDemoMode = () => {
    // Use a fixed Qibla direction for demo
    const fakeQibla = 120;
    // Remove the useState and useEffect hooks from here
    
    return (
      <div className={`min-h-screen flex flex-col relative overflow-x-hidden ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-100'}`}>
        {/* Background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {isDarkMode ? (
            <>
              <div className="absolute -top-1/4 -left-1/4 w-full max-w-[800px] h-full max-h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
              <div className="absolute -bottom-1/4 -right-1/4 w-full max-w-[800px] h-full max-h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-gray-100 via-emerald-100/20 to-gray-100" />
              <div className="absolute inset-0 bg-gradient-to-b from-gray-100/90 via-transparent to-gray-100/90" />
              <div className="absolute inset-0 backdrop-blur-[50px]" />
              <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("/images/bgpattern.png")', backgroundSize: '400px', filter: 'invert(1)', mixBlendMode: 'multiply' }}></div>
            </>
          )}
        </div>
        
        {/* Alignment Status Pill - Apple-like floating indicator at top */}
        {demoAlignmentStatus !== 'none' && (
          <div className="fixed top-0 left-0 right-0 z-30 flex justify-center" 
               style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
            <div 
              className={`px-4 py-2 rounded-full shadow-lg backdrop-blur-xl flex items-center gap-2 transition-all duration-300 ${
                demoAlignmentStatus === 'aligned' 
                  ? 'opacity-100 transform translate-y-0' 
                  : 'opacity-75 transform -translate-y-1'
              } ${
                isDarkMode 
                  ? 'bg-[#111111]/90' 
                  : 'bg-white/90'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${
                demoAlignmentStatus === 'aligned' 
                  ? 'bg-green-500 animate-pulse' 
                  : demoAlignmentStatus === 'close' 
                    ? 'bg-yellow-500' 
                    : 'bg-gray-400'
              }`} />
              <Kaaba01Icon className={`w-4 h-4 ${demoAlignmentStyles.text}`} />
              <span className={`text-sm ${demoAlignmentStyles.text}`}>
                {demoAlignmentStatus === 'aligned' ? 'Perfectly aligned' : 
                 demoAlignmentStatus === 'close' ? 'Very close to Qibla' : 
                 demoAlignmentStatus === 'near' ? 'Getting close to Qibla' : ''}
              </span>
            </div>
          </div>
        )}
        
        <div className="relative z-10 flex flex-col h-full">
          <header className="flex-none">
            <div style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }} className="px-4 py-4">
              <div className="flex items-center justify-between">
                <h1 className={`${isCompactView ? 'text-xl' : 'text-2xl'} font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Qibla Finder</h1>
              </div>
            </div>
          </header>

          <div className="flex-1 flex flex-col px-4" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 84px)' }}>
            <div className={`h-full flex flex-col transition-all duration-200 rounded-[24px] p-6 ${isDarkMode ? 'bg-[#111111]/80 backdrop-blur-xl shadow-lg shadow-black/5' : 'bg-white/80 backdrop-blur-xl shadow-sm'}`}> 
              <div className="flex-1 flex items-center justify-center min-h-0">
                <div className="flex flex-col items-center w-full max-w-md">
                  {/* Compass - Match the real qibla finder's design */}
                  <div className={`relative aspect-square ${isCompactView ? 'w-52' : 'w-64'} ${isCompactView ? 'mb-4' : 'mb-8'}`}>
                    {/* Compass glow effect */}
                    {(demoAlignmentStatus === 'aligned' || demoAlignmentStatus === 'close') && (
                      <div className={`absolute -inset-4 rounded-full transition-opacity duration-500 ${
                        demoAlignmentStatus === 'aligned' ? 'opacity-80' : 'opacity-40'
                      } blur-xl ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-500/5'}`} />
                    )}
                    
                    <div 
                      className={`absolute inset-0 rounded-full border-4 transition-all duration-200 ${ 
                        demoAlignmentStatus === 'aligned' ? (isDarkMode ? 'border-emerald-500 bg-[#111111]/90' : 'border-emerald-500 bg-white/90') : (isDarkMode ? 'border-gray-800 bg-[#111111]/90' : 'border-gray-200 bg-white/90')
                      } backdrop-blur-sm ${demoAlignmentStyles.ring}`} 
                      style={{ transform: `rotate(${-demoDeviceDirection}deg)`, transition: 'transform 0.1s linear' }}
                    >
                      {/* Compass internals (lines, letters, Qibla indicator) */}
                      <div className="absolute inset-0">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className={`absolute w-0.5 h-full bg-gradient-to-b ${isDarkMode ? 'from-transparent via-emerald-500 to-transparent' : 'from-transparent via-emerald-600 to-transparent'}`} />
                            <div className={`absolute w-full h-0.5 bg-gradient-to-r ${isDarkMode ? 'from-transparent via-emerald-500 to-transparent' : 'from-transparent via-emerald-600 to-transparent'}`} />
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className={`absolute top-4 font-medium ${isCompactView ? 'text-xs' : 'text-sm'} ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>N</span>
                            <span className={`absolute right-4 font-medium ${isCompactView ? 'text-xs' : 'text-sm'} ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>E</span>
                            <span className={`absolute bottom-4 font-medium ${isCompactView ? 'text-xs' : 'text-sm'} ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>S</span>
                            <span className={`absolute left-4 font-medium ${isCompactView ? 'text-xs' : 'text-sm'} ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>W</span>
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center rotate-45">
                            <div className={`absolute w-0.5 h-full bg-gradient-to-b ${isDarkMode ? 'from-transparent via-emerald-500/50 to-transparent' : 'from-transparent via-emerald-600/50 to-transparent'}`} />
                            <div className={`absolute w-full h-0.5 bg-gradient-to-r ${isDarkMode ? 'from-transparent via-emerald-500/50 to-transparent' : 'from-transparent via-emerald-600/50 to-transparent'}`} />
                          </div>
                          {[...Array(24)].map((_, i) => (
                            <div key={i} className="absolute inset-0 flex items-center justify-center" style={{ transform: `rotate(${i * 15}deg)` }}>
                              <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-2 ${isDarkMode ? 'bg-emerald-500/30' : 'bg-emerald-600/30'}`} />
                            </div>
                          ))}
                          <div className={`absolute inset-2 rounded-full border ${demoAlignmentStatus === 'aligned' ? 'border-emerald-500' : (isDarkMode ? 'border-emerald-500/20' : 'border-emerald-600/20')}`} />
                        </div>
                        {/* Qibla Indicator */}
                        <div className="absolute inset-0">
                          <div className="absolute w-full h-full flex items-center justify-center" style={{ transform: `rotate(${fakeQibla}deg)` }}>
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 flex flex-col items-center">
                              <ArrowUp01Icon className={isDarkMode ? 'text-emerald-500' : 'text-emerald-600'} size={isCompactView ? 24 : 28} />
                              <Kaaba01Icon className={isDarkMode ? 'text-emerald-500' : 'text-emerald-600'} size={isCompactView ? 16 : 20} style={{ marginTop: '-4px' }} />
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Center dot */}
                      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full shadow-lg ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`} />
                    </div>
                    
                    {/* Alignment indicator ring */}
                    {demoAlignmentStatus !== 'none' && (
                      <div className={`absolute inset-0 rounded-full border-2 transition-all duration-500 ${
                        demoAlignmentStatus === 'aligned' 
                          ? (isDarkMode ? 'border-emerald-500 scale-[1.03]' : 'border-emerald-600 scale-[1.03]')
                          : demoAlignmentStatus === 'close'
                            ? (isDarkMode ? 'border-emerald-500/50 scale-[1.02]' : 'border-emerald-600/50 scale-[1.02]')
                            : (isDarkMode ? 'border-emerald-500/30 scale-[1.01]' : 'border-emerald-600/30 scale-[1.01]')
                      }`} />
                    )}
                  </div>
                  {/* Data Display - Smaller on iPad in phone app mode */}
                  <div className={`${isCompactView ? 'space-y-2' : 'space-y-4'} w-full`}>
                    <div className={`flex items-center justify-between ${isCompactView ? 'p-3' : 'p-4'} rounded-xl backdrop-blur-sm ${isDarkMode ? 'bg-white/5' : 'bg-gray-50/80'}`}> 
                      <div className="flex items-center gap-3">
                        <Clock className={isDarkMode ? "text-emerald-400" : "text-emerald-600"} size={isCompactView ? 16 : 20} />
                        <span className={`${isCompactView ? 'text-sm' : 'text-base'} ${isDarkMode ? "text-white" : "text-gray-900"}`}>Direction</span>
                      </div>
                      <span className={`font-medium ${isCompactView ? 'text-sm' : 'text-base'} ${isDarkMode ? "text-white" : "text-gray-900"}`}> 
                        {Math.round(demoDeviceDirection)}°
                      </span>
                    </div>
                    <div className={`flex items-center justify-between ${isCompactView ? 'p-3' : 'p-4'} rounded-xl backdrop-blur-sm ${isDarkMode ? 'bg-white/5' : 'bg-gray-50/80'}`}> 
                      <div className="flex items-center gap-3">
                        <Compass className={isDarkMode ? "text-emerald-400" : "text-emerald-600"} size={isCompactView ? 16 : 20} />
                        <span className={`${isCompactView ? 'text-sm' : 'text-base'} ${isDarkMode ? "text-white" : "text-gray-900"}`}>Qibla Angle</span>
                      </div>
                      <span className={`font-medium ${isCompactView ? 'text-sm' : 'text-base'} ${isDarkMode ? "text-white" : "text-gray-900"}`}> 
                        {Math.round(fakeQibla)}°
                      </span>
                    </div>
                    <div className={`flex items-center justify-between ${isCompactView ? 'p-3' : 'p-4'} rounded-xl backdrop-blur-sm ${isDarkMode ? 'bg-white/5' : 'bg-gray-50/80'}`}> 
                      <div className="flex items-center gap-3">
                        <MapPin className={isDarkMode ? "text-emerald-400" : "text-emerald-600"} size={isCompactView ? 16 : 20} />
                        <span className={`${isCompactView ? 'text-sm' : 'text-base'} ${isDarkMode ? "text-white" : "text-gray-900"}`}>Location</span>
                      </div>
                      <span className={`font-medium ${isCompactView ? 'text-sm' : 'text-base'} ${isDarkMode ? "text-white" : "text-gray-900"}`}> 
                        Demo Mode
                      </span>
                    </div>
                  </div>
                  <p className={`${isCompactView ? 'mt-4' : 'mt-8'} ${isCompactView ? 'text-sm' : 'text-base'} text-gray-600 dark:text-gray-400 text-center`}>
                    Qibla Finder is running in <b>demo mode</b> because device sensors are not available.
                    {!isCompactView && <br />}
                    On a real device, you will see live direction.
                  </p>
                  {/* Subtle waiting for sensor indicator */}
                  {showSensorWait && (
                    <div className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium text-center transition-all duration-300 ${isDarkMode ? 'bg-yellow-900/30 text-yellow-200 border border-yellow-800' : 'bg-yellow-50/80 text-yellow-700 border border-yellow-200'}`} style={{ maxWidth: 320 }}>
                      Waiting for sensor data...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Determine what to render based on stableComponentState instead of componentState
  if (stableComponentState === 'demoMode' || demoMode) {
    return renderDemoMode();
  }
  if (stableComponentState === 'initializing' ||
      stableComponentState === 'checkingPermissions' ||
      stableComponentState === 'requestingPermissions' ||
      stableComponentState === 'gettingLocation' ||
      stableComponentState === 'waitingForCompass') {
    // Use the updated loading renderer
    return renderLoadingState(stableComponentState);
  }
  
  // Handle specific error states
  if (stableComponentState === 'permissionsDenied' ||
      stableComponentState === 'locationError' ||
      stableComponentState === 'compassError') {
    return renderErrorState(error || 'An unexpected error occurred.');
  }

  // Main Ready State (render the compass UI)
  // Ensure we have both directions before rendering the ready state
  if (stableComponentState === 'ready' && qiblaDirection !== null && deviceDirection !== null) {
    const alignmentStyles = getAlignmentStyles();
    const alignmentMessage = getAlignmentMessage();

    return (
      <div className={`min-h-screen flex flex-col relative overflow-x-hidden ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-100'}`}>
        {/* Background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {isDarkMode ? (
            <>
              <div className="absolute -top-1/4 -left-1/4 w-full max-w-[800px] h-full max-h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
              <div className="absolute -bottom-1/4 -right-1/4 w-full max-w-[800px] h-full max-h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-gray-100 via-emerald-100/20 to-gray-100" />
              <div className="absolute inset-0 bg-gradient-to-b from-gray-100/90 via-transparent to-gray-100/90" />
              <div className="absolute inset-0 backdrop-blur-[50px]" />
              <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("/images/bgpattern.png")', backgroundSize: '400px', filter: 'invert(1)', mixBlendMode: 'multiply' }}></div>
            </>
          )}
        </div>

        {/* Alignment Status Pill - Apple-like floating indicator at top */}
        {alignmentStatus !== 'none' && (
          <div className="fixed top-0 left-0 right-0 z-30 flex justify-center" 
               style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
            <div 
              className={`px-4 py-2 rounded-full shadow-lg backdrop-blur-xl flex items-center gap-2 transition-all duration-300 ${
                alignmentStatus === 'aligned' 
                  ? 'opacity-100 transform translate-y-0' 
                  : 'opacity-75 transform -translate-y-1'
              } ${
                isDarkMode 
                  ? 'bg-[#111111]/90' 
                  : 'bg-white/90'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${
                alignmentStatus === 'aligned' 
                  ? 'bg-green-500 animate-pulse' 
                  : alignmentStatus === 'close' 
                    ? 'bg-yellow-500' 
                    : 'bg-gray-400'
              }`} />
              <Kaaba01Icon className={`w-4 h-4 ${alignmentStyles.text}`} />
              <span className={`text-sm ${alignmentStyles.text}`}>
                {alignmentMessage}
              </span>
            </div>
          </div>
        )}

        <div className="relative z-10 flex flex-col h-full">
          <header className="flex-none">
            <div style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }} className="px-4 py-4">
              <div className="flex items-center justify-between">
                <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Qibla Finder</h1>
              </div>
            </div>
          </header>

          <div className="flex-1 flex flex-col px-4" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 84px)' }}>
            <div className={`h-full flex flex-col transition-all duration-200 rounded-[24px] p-6 ${isDarkMode ? 'bg-[#111111]/80 backdrop-blur-xl shadow-lg shadow-black/5' : 'bg-white/80 backdrop-blur-xl shadow-sm'}`}> 
              <div className="flex-1 flex items-center justify-center min-h-0">
                <div className="flex flex-col items-center w-full max-w-md">
                  {/* Compass - Use pre-calculated alignmentStyles */}
                  <div className="relative aspect-square w-full mb-8">
                    {/* Compass glow effect */}
                    {(alignmentStatus === 'aligned' || alignmentStatus === 'close') && (
                      <div className={`absolute -inset-4 rounded-full transition-opacity duration-500 ${
                        alignmentStatus === 'aligned' ? 'opacity-80' : 'opacity-40'
                      } blur-xl ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-500/5'}`} />
                    )}
                    
                    <div 
                      className={`absolute inset-0 rounded-full border-4 transition-all duration-200 ${ 
                        alignmentStatus === 'aligned' ? (isDarkMode ? 'border-emerald-500 bg-[#111111]/90' : 'border-emerald-500 bg-white/90') : (isDarkMode ? 'border-gray-800 bg-[#111111]/90' : 'border-gray-200 bg-white/90')
                      } backdrop-blur-sm ${alignmentStyles.ring}`} 
                      style={{ transform: `rotate(${-deviceDirection}deg)`, transition: 'transform 0.1s linear' }}
                    >
                      {/* Compass internals (lines, letters, Qibla indicator) */}
                       <div className="absolute inset-0">
                         <div className="absolute inset-0 flex items-center justify-center">
                           <div className="absolute inset-0 flex items-center justify-center">
                             <div className={`absolute w-0.5 h-full bg-gradient-to-b ${isDarkMode ? 'from-transparent via-emerald-500 to-transparent' : 'from-transparent via-emerald-600 to-transparent'}`} />
                             <div className={`absolute w-full h-0.5 bg-gradient-to-r ${isDarkMode ? 'from-transparent via-emerald-500 to-transparent' : 'from-transparent via-emerald-600 to-transparent'}`} />
                           </div>
                           <div className="absolute inset-0 flex items-center justify-center">
                             <span className={`absolute top-4 font-medium text-sm ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>N</span>
                             <span className={`absolute right-4 font-medium text-sm ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>E</span>
                             <span className={`absolute bottom-4 font-medium text-sm ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>S</span>
                             <span className={`absolute left-4 font-medium text-sm ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>W</span>
                           </div>
                           <div className="absolute inset-0 flex items-center justify-center rotate-45">
                             <div className={`absolute w-0.5 h-full bg-gradient-to-b ${isDarkMode ? 'from-transparent via-emerald-500/50 to-transparent' : 'from-transparent via-emerald-600/50 to-transparent'}`} />
                             <div className={`absolute w-full h-0.5 bg-gradient-to-r ${isDarkMode ? 'from-transparent via-emerald-500/50 to-transparent' : 'from-transparent via-emerald-600/50 to-transparent'}`} />
                           </div>
                           {[...Array(24)].map((_, i) => (
                             <div key={i} className="absolute inset-0 flex items-center justify-center" style={{ transform: `rotate(${i * 15}deg)` }}>
                               <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-2 ${isDarkMode ? 'bg-emerald-500/30' : 'bg-emerald-600/30'}`} />
                             </div>
                           ))}
                           <div className={`absolute inset-2 rounded-full border ${alignmentStatus === 'aligned' ? 'border-emerald-500' : (isDarkMode ? 'border-emerald-500/20' : 'border-emerald-600/20')}`} />
                         </div>
                         {/* Qibla Indicator */}
                         {qiblaDirection !== null && (
                           <div className="absolute inset-0">
                             <div className="absolute w-full h-full flex items-center justify-center" style={{ transform: `rotate(${qiblaDirection}deg)` }}>
                               <div className="absolute top-0 left-1/2 -translate-x-1/2 flex flex-col items-center">
                                 <ArrowUp01Icon className={isDarkMode ? 'text-emerald-500' : 'text-emerald-600'} size={28} />
                                 <Kaaba01Icon className={isDarkMode ? 'text-emerald-500' : 'text-emerald-600'} size={20} style={{ marginTop: '-4px' }} />
                               </div>
                             </div>
                           </div>
                         )}
                       </div>
                      {/* Center dot */}
                      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full shadow-lg ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`} />
                    </div>
                    
                    {/* Alignment indicator ring */}
                    {alignmentStatus !== 'none' && (
                      <div className={`absolute inset-0 rounded-full border-2 transition-all duration-500 ${
                        alignmentStatus === 'aligned' 
                          ? (isDarkMode ? 'border-emerald-500 scale-[1.03]' : 'border-emerald-600 scale-[1.03]')
                          : alignmentStatus === 'close'
                            ? (isDarkMode ? 'border-emerald-500/50 scale-[1.02]' : 'border-emerald-600/50 scale-[1.02]')
                            : (isDarkMode ? 'border-emerald-500/30 scale-[1.01]' : 'border-emerald-600/30 scale-[1.01]')
                      }`} />
                    )}
                  </div>
                  {/* Data Display */}
                   <div className="space-y-4 w-full">
                     <div className={`flex items-center justify-between p-4 rounded-xl backdrop-blur-sm ${isDarkMode ? 'bg-white/5' : 'bg-gray-50/80'}`}> 
                       <div className="flex items-center gap-3">
                         <Clock className={isDarkMode ? "text-emerald-400" : "text-emerald-600"} size={20} />
                         <span className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>Direction</span>
                       </div>
                       <span className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}> 
                         {Math.round(deviceDirection)}°
                       </span>
                     </div>
                     <div className={`flex items-center justify-between p-4 rounded-xl backdrop-blur-sm ${isDarkMode ? 'bg-white/5' : 'bg-gray-50/80'}`}> 
                       <div className="flex items-center gap-3">
                         <Compass className={isDarkMode ? "text-emerald-400" : "text-emerald-600"} size={20} />
                         <span className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>Qibla Angle</span>
                       </div>
                       <span className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}> 
                         {Math.round(qiblaDirection)}°
                       </span>
                     </div>
                     <div className={`flex items-center justify-between p-4 rounded-xl backdrop-blur-sm ${isDarkMode ? 'bg-white/5' : 'bg-gray-50/80'}`}> 
                       <div className="flex items-center gap-3">
                         <MapPin className={isDarkMode ? "text-emerald-400" : "text-emerald-600"} size={20} />
                         <span className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>Location</span>
                       </div>
                       <span className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}> 
                         {location ? `${location.latitude.toFixed(4)}°, ${location.longitude.toFixed(4)}°` : 'N/A'}
                       </span>
                     </div>
                   </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Fallback: If none of the states match (should ideally not happen), show a generic loading state.
  console.warn("[QiblaFinder] Reached fallback rendering state. Current state:", stableComponentState);
  return renderLoadingState('initializing'); 

};

export default QiblaFinder;